// Phase 23B.5.1 — companion IndexedDB store for offline photo blobs.
//
// Keyed by `client_temp_id` from the outbox row so the drain flow can
// look up the blob by the same key that idempotency uses. Blobs are
// uploaded FIRST when the row is drained, and deleted immediately on
// successful outlet create.
//
// Storage caps:
//   * per-blob:  MAX_BLOB_BYTES  (4 MB)
//   * total:     MAX_TOTAL_BYTES (50 MB) OR MAX_BLOBS (20)  — whichever hits
//
// Persistence: navigator.storage.persist() is called best-effort on first put.

import { openDB } from "idb";

const BLOB_DB = "tulsi_offline_blobs";
const BLOB_STORE = "outbox_blobs";
export const MAX_BLOB_BYTES = 4 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
export const MAX_BLOBS = 20;

let _persistTried = false;

async function _db() {
  return openDB(BLOB_DB, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        const s = db.createObjectStore(BLOB_STORE, { keyPath: "client_temp_id" });
        s.createIndex("by_created", "created_at");
      }
    },
  });
}

async function _requestPersist() {
  if (_persistTried) return;
  _persistTried = true;
  try {
    if (navigator.storage?.persist) await navigator.storage.persist();
  } catch { /* ignore */ }
}

export async function totalUsage() {
  const db = await _db();
  const rows = await db.getAll(BLOB_STORE);
  return {
    count: rows.length,
    bytes: rows.reduce((n, r) => n + (r.size_bytes || 0), 0),
  };
}

export class BlobCapError extends Error {
  constructor(kind, msg) { super(msg); this.kind = kind; }
}

export async function putBlob({ client_temp_id, blob, blob_type = "storefront_photo" }) {
  if (!blob) throw new BlobCapError("empty", "No blob to save");
  if (blob.size > MAX_BLOB_BYTES) {
    throw new BlobCapError("too_big",
      `Photo is ${(blob.size / 1024 / 1024).toFixed(1)} MB — max ${MAX_BLOB_BYTES / 1024 / 1024} MB`);
  }
  const { count, bytes } = await totalUsage();
  if (count >= MAX_BLOBS) {
    throw new BlobCapError("blob_cap",
      `Offline photo store is full (${MAX_BLOBS} blobs). Sync existing drafts first.`);
  }
  if (bytes + blob.size > MAX_TOTAL_BYTES) {
    throw new BlobCapError("size_cap",
      `Offline photo store is full (${(MAX_TOTAL_BYTES / 1024 / 1024).toFixed(0)} MB). Sync existing drafts first.`);
  }
  await _requestPersist();
  const db = await _db();
  await db.put(BLOB_STORE, {
    client_temp_id, blob, blob_type,
    size_bytes: blob.size,
    created_at: new Date().toISOString(),
  });
  return { ok: true, size_bytes: blob.size };
}

export async function getBlob(client_temp_id) {
  const db = await _db();
  return await db.get(BLOB_STORE, client_temp_id);
}

export async function deleteBlob(client_temp_id) {
  const db = await _db();
  await db.delete(BLOB_STORE, client_temp_id);
}

export async function listAllBlobs() {
  const db = await _db();
  return await db.getAll(BLOB_STORE);
}
